import { createClient } from './client';

/**
 * Uploads a file to Supabase Storage
 * @param file The file to upload
 * @param folder The folder in the 'media' bucket ('images' | 'videos')
 * @returns The public URL of the uploaded file
 */
export async function uploadFile(file: File, folder: 'images' | 'videos'): Promise<string> {
  const supabase = createClient();
  
  // Validate file size
  const maxSize = folder === 'images' ? 5 * 1024 * 1024 : 20 * 1024 * 1024; // 5MB for images, 20MB for videos
  if (file.size > maxSize) {
    throw new Error(`File size must be less than ${folder === 'images' ? '5MB' : '20MB'}`);
  }

  // Validate file type
  if (!file.type.startsWith(folder === 'images' ? 'image/' : 'video/')) {
    throw new Error(`File must be an ${folder === 'images' ? 'image' : 'video'}`);
  }

  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
  const filePath = `${folder}/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('media')
    .upload(filePath, file);

  if (uploadError) {
    throw uploadError;
  }

  const { data: { publicUrl } } = supabase.storage
    .from('media')
    .getPublicUrl(filePath);

  return publicUrl;
}

/**
 * Deletes a file from Supabase Storage
 * @param fileUrl The full public URL of the file to delete
 */
export async function deleteFile(fileUrl: string): Promise<void> {
  if (!fileUrl || !fileUrl.includes('/storage/v1/object/public/media/')) return;

  const supabase = createClient();
  const filePathMatch = fileUrl.split('/storage/v1/object/public/media/');
  
  if (filePathMatch.length > 1) {
    const filePath = filePathMatch[1];
    
    // Attempt to delete it
    const { error } = await supabase.storage
      .from('media')
      .remove([filePath]);
      
    if (error) {
      console.error('Failed to delete file from storage:', error);
    }
  }
}
